import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function CategorySection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const featuredCategories = categories.slice(0, 6);
  const restCategories = categories.slice(6);

  return (
    <section id="kategorie" className="py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            Przeglądaj kategorie produktów
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            Wybierz interesującą Cię kategorię i odkryj najlepsze produkty dostępne w Kakobuy Spreadsheet
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap"
          >
            <i className="ri-file-list-3-line text-base"></i>
            Kakobuy Spreadsheet
          </a>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 rounded-lg transition-all whitespace-nowrap"
          >
            <i className="ri-shopping-cart-line text-base"></i>
            Buy It Now
          </a>
        </div>

        <div className="relative mb-10 max-w-md mx-auto" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between px-5 py-3.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-800 hover:border-primary-300 transition-colors"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-base text-primary-500"></i>
              <span>Wybierz kategorię produktów</span>
            </span>
            <i className={`transition-transform text-base ${dropdownOpen ? 'rotate-180' : ''} ri-arrow-down-s-line`}></i>
          </button>

          {dropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200/70 rounded-lg shadow-sm z-30 max-h-72 overflow-y-auto">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                    <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                  </div>
                  {cat.name}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {featuredCategories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group relative overflow-hidden rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all p-5 flex items-center gap-4"
            >
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={cat.imageUrl}
                  alt={cat.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground-900 text-sm md:text-base group-hover:text-primary-600 transition-colors whitespace-nowrap">
                  {cat.name}
                </p>
                <p className="text-xs text-foreground-500 mt-1 flex items-center gap-1">
                  <i className="ri-external-link-line text-xs"></i>
                  Zobacz produkty
                </p>
              </div>
              <div className="w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-arrow-right-line text-primary-500"></i>
              </div>
            </a>
          ))}
        </div>

        {restCategories.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 mt-5">
            {restCategories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-600 bg-background-100 border border-background-200/70 rounded-lg hover:border-primary-200 hover:text-primary-600 transition-all whitespace-nowrap group"
              >
                <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                  <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                </div>
                {cat.name}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}